import React from "react";
import NoDataComponent from "./noData";
import { CIcon } from "@coreui/icons-react";
import * as icon from "@coreui/icons";
import ColorCell from "./colorCell";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DownloadOutlined, FullscreenOutlined, UpOutlined, DownOutlined, CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";

import * as XLSX from "xlsx-js-style";

function TableContainer(props) {
  const {
    fields,
    getVisibleColumns,
    sortConfig,
    toggleSort,
    getFieldLabel,
    hasActiveFilter,
    handleColumnFilterClick,
    sortedData,
    renderGroup,
    dataTotal,
    formatNumber,
    METRIC_FORMATS,
    columnFilterPopup,
    columnPopupRef,
    ColumnFilterPopupContent,
    filters,
    updateCondition,
    onCloseColumnFilterPopup,
    showPopup,
    togglePopup,
    rowGroupSortDirections,
    onRowGroupSortChange,
    fieldsPanel: {
      FilterField,
      moveField,
      toggleCheck,
    } = {},
    showFilterPanel,
    setShowFilterPanel,
    columnsPanel: {
      searchTerm,
      setSearchTerm,
      columnOrder,
      setAllColumnsVisible,
      ColumnField,
    } = {},
    headerDrag: { moveColumn } = {},
    columnWidths = {},
    setColumnWidth,
    onSaveSettings,
    applyRowGroupChanges,
    hasPendingRowGroupChanges,
  } = props;

  const [resizing, setResizing] = React.useState({ active: false, field: null, startX: 0, startWidth: 0 });

  const containerRef = React.useRef(null);
  const tableRef = React.useRef(null);
  const tbodyRef = React.useRef(null);
  const columnsPanelRef = React.useRef(null);
  const filtersPanelRef = React.useRef(null);
  const fieldListRef = React.useRef(null);
  const lastFieldListScrollTopRef = React.useRef(0);

  React.useEffect(() => {
    const el = fieldListRef && fieldListRef.current;
    if (!el) return;
    if (typeof lastFieldListScrollTopRef.current === 'number' && el.scrollTop !== lastFieldListScrollTopRef.current) {
      try {
        el.scrollTop = lastFieldListScrollTopRef.current;
      } catch (_) {}
    }
  });

  const handleExport = React.useCallback(() => {
    try {
      const tableEl = tableRef.current;
      if (!tableEl) return;

      
      const rgbToHex = (rgb) => {
        if (!rgb) return undefined;
        const m = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (!m) return undefined;
        const r = Number(m[1]).toString(16).padStart(2, "0");
        const g = Number(m[2]).toString(16).padStart(2, "0");
        const b = Number(m[3]).toString(16).padStart(2, "0");
        return (r + g + b).toUpperCase();
      };

      const rows = tableEl.querySelectorAll("thead tr, tbody tr, tfoot tr");
      const ws = {};
      let maxC = 0;
      
      const grid = [];

      const setCell = (r, c, v, s, merge) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        ws[addr] = { v: String(v ?? ""), t: "s" };
        if (s) ws[addr].s = s;
        if (merge) {
          ws['!merges'] = ws['!merges'] || [];
          ws['!merges'].push(merge);
        }
      };

      rows.forEach((tr, rIndex) => {
        grid[rIndex] = grid[rIndex] || [];
        let cIndex = 0;
        
        const cells = tr.querySelectorAll("th, td");
        cells.forEach((cellEl) => {
          while (grid[rIndex][cIndex]) cIndex += 1;

          const colspan = parseInt(cellEl.getAttribute("colspan") || "1", 10);
          const rowspan = parseInt(cellEl.getAttribute("rowspan") || "1", 10);
          
          let value = (cellEl.innerText || "").replace(/\u00A0/g, ' ').trim();
          if (value === '' ) {
            
            const dataValAttr = cellEl.getAttribute('data-export-value');
            if (dataValAttr !== null) value = String(dataValAttr);
          }

          const cs = window.getComputedStyle(cellEl);
          const bg = rgbToHex(cs.backgroundColor);
          const color = rgbToHex(cs.color);
          const style = {};
          if (bg) style.fill = { patternType: "solid", fgColor: { rgb: bg } };
          if (color) style.font = { color: { rgb: color } };

          
          const isFirstRow = rIndex === 0;
          const isLastRow = rIndex === rows.length - 1;
          const firstCellEl = tr.cells && tr.cells[0] ? tr.cells[0] : null;
          const hasToggleInFirstCell = firstCellEl && !!firstCellEl.querySelector('.fa-chevron-right, .fa-chevron-down, .fa-caret-right, .fa-caret-down, [data-toggle="expand"], .expand-toggle');
          const isParentRow = (tr.getAttribute('data-has-children') === 'true') || hasToggleInFirstCell;
          if (isFirstRow || isLastRow || (cIndex === 0 && isParentRow)) {
            style.font = { ...(style.font || {}), bold: true };
          }

          
          setCell(rIndex, cIndex, value, style, (colspan > 1 || rowspan > 1) ? {
            s: { r: rIndex, c: cIndex },
            e: { r: rIndex + rowspan - 1, c: cIndex + colspan - 1 },
          } : undefined);

          
          for (let r = 0; r < rowspan; r += 1) {
            grid[rIndex + r] = grid[rIndex + r] || [];
            for (let c = 0; c < colspan; c += 1) {
              grid[rIndex + r][cIndex + c] = true;
            }
          }

          cIndex += colspan;
          if (cIndex > maxC) maxC = cIndex;
        });
      });

      ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length - 1, c: maxC - 1 } });

      
      try {
        const headerRow = tableEl.querySelector('thead tr');
        if (headerRow) {
          const ths = headerRow.querySelectorAll('th');
          const cols = [];
          let colIndex = 0;
          ths.forEach((th) => {
            const colspan = parseInt(th.getAttribute('colspan') || '1', 10);
            const totalWidth = th.getBoundingClientRect().width || th.offsetWidth || 0;
            const perCol = Math.max(30, Math.round(totalWidth / Math.max(1, colspan))); 
            for (let i = 0; i < colspan; i += 1) {
              cols[colIndex] = { wpx: perCol };
              colIndex += 1;
            }
          });
          if (cols.length) ws['!cols'] = cols;
        }
      } catch (_) {
        
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      XLSX.writeFile(wb, "report.xlsx");
    } catch (e) {
      
      console.error("Export failed:", e);
    }
  }, []);

  const handleToggleFullscreen = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  }, []);



  React.useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizing.active || !resizing.field) return;
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(80, resizing.startWidth + delta);
      if (setColumnWidth) setColumnWidth(resizing.field, newWidth);
    };
    const onMouseUp = () => {
      if (resizing.active) setResizing({ active: false, field: null, startX: 0, startWidth: 0 });
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [resizing, setColumnWidth]);

  React.useEffect(() => {
    const handleOutsideClick = (e) => {
      if (showPopup && columnsPanelRef.current && !columnsPanelRef.current.contains(e.target)) {
        if (togglePopup) togglePopup();
      }
      if (showFilterPanel && filtersPanelRef.current && !filtersPanelRef.current.contains(e.target)) {
        if (setShowFilterPanel) setShowFilterPanel(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showPopup, showFilterPanel, togglePopup, setShowFilterPanel]);

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        {onSaveSettings && (
          <div className="me-2">
            <Tooltip title="Save config columns">
              <Button type="dashed" icon={<SaveOutlined />} onClick={onSaveSettings} />
            </Tooltip>
          </div>
        )}
        <div className="me-2">
          <Tooltip title="Export">
            <Button type="dashed" icon={<DownloadOutlined />} onClick={handleExport} />
          </Tooltip>
        </div>
        <div>
          <Tooltip title="Full Screen">
            <Button type="dashed" icon={<FullscreenOutlined />} onClick={handleToggleFullscreen} />
          </Tooltip>
        </div>
      </div>
      <div className="table-container d-flex" ref={containerRef}>
        <div className="table-scroll-wrapper">
          <table
            ref={tableRef}
            className="table table-bordered table-hover tb-campaign-center"
            style={{ tableLayout: "fixed" }}
          >
            <thead className="thead-light table-fixed-header">
              <tr key="t-head1">
                <th
                  className="sticky-col-left th-text-filter"
                  style={{ width: `${columnWidths.__group__ || 400}px`, height: "50px" }}
                  colSpan="2"
                >
                  {fields
                    .filter((f) => f.checked)
                    .map((f, i, arr) => (
                      <span key={f.key} className="selected-label">
                        {f.label}
                        {i < arr.length - 1 && (
                          <span className="comma">, </span>
                        )}
                      </span>
                    ))}
                  {setColumnWidth && (
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const startWidth = columnWidths.__group__ || 400;
                        setResizing({ active: true, field: "__group__", startX: e.clientX, startWidth });
                      }}
                      style={{ position: "absolute", top: 0, right: 0, width: "6px", height: "100%", cursor: "col-resize", userSelect: "none" }}
                    />
                  )}
                </th>
                {getVisibleColumns().map((field, idx) => (
                  <th
                    key={`th-${field}`}
                    className="text-center"
                    style={{ width: `${columnWidths[field] || 170}px`, height: "50px", cursor: "move", position: "relative" }}
                    draggable={true}
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", String(field));
                      if (e.dataTransfer.effectAllowed !== undefined) {
                        e.dataTransfer.effectAllowed = "move";
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      try { e.dataTransfer.dropEffect = "move"; } catch (_) {}
                    }}
                    onDrop={(e) => {
                      const fromField = e.dataTransfer.getData("text/plain");
                      if (fromField && fromField !== field) {
                        moveColumn(fromField, field);
                      }
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div
                        onClick={() => toggleSort(field)}
                        className="th-text-filter"
                      >
                        {getFieldLabel(field)}{" "}
                        <i
                          style={{ fontSize: "13px" }}
                          className={`fa-solid ${
                            sortConfig.field === field
                              ? sortConfig.direction === "asc"
                                ? "fa-arrow-up text-success"
                                : "fa-arrow-down text-success"
                              : "fa-arrow-up text-muted"
                          }`}
                        />
                      </div>
                      <div
                        className={`d-flex ${
                          hasActiveFilter(field) ? "ag-filter-active" : ""
                        }`}
                        onClick={(e) => handleColumnFilterClick(field, e)}
                        style={{ cursor: "pointer" }}
                      >
                        <CIcon
                          icon={icon.cilListFilter}
                          style={{ width: "16px", height: "16px" }}
                        />
                      </div>
                    </div>
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const startWidth = columnWidths[field] || 170;
                        setResizing({ active: true, field, startX: e.clientX, startWidth });
                      }}
                      style={{ position: "absolute", top: 0, right: 0, width: "6px", height: "100%", cursor: "col-resize", userSelect: "none" }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody ref={tbodyRef}>
              {sortedData.length > 0 ? (
                renderGroup(sortedData)
              ) : (
                <tr>
                  <td colSpan="16" className="text-center">
                    <NoDataComponent />
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="table-fixed-footer">
              <tr className="table-secondary fw-bold" key="t-footer-1">
                <td className="sticky-col-left" colSpan="2" style={{ width: `${columnWidths.__group__ || 400}px` }}>
                  Total
                </td>
                {getVisibleColumns().map((field) => {
                  const cfg = METRIC_FORMATS[field] || {};
                  const raw = dataTotal && dataTotal[field] !== undefined ? dataTotal[field] : 0;
                  const num = parseFloat(raw) || 0;
                  const formatted = `${cfg.prefix || ''}${formatNumber(num)}${cfg.suffix || ''}`;
                  return (
                    <td key={`total-${field}`} className="text-center" style={{ width: `${columnWidths[field] || 170}px` }}>
                      {formatted}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>

        {columnFilterPopup?.activeColumn && (
          <div
            ref={columnPopupRef}
            style={{
              position: "fixed",
              top: columnFilterPopup.position.top,
              left: columnFilterPopup.position.left,
              zIndex: 1050,
              background: "#fff",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.08)",
            }}
          >
            <ColumnFilterPopupContent
              field={columnFilterPopup.activeColumn}
              filters={filters}
              updateCondition={updateCondition}
              getFieldLabel={getFieldLabel}
              onClose={onCloseColumnFilterPopup}
            />
          </div>
        )}

        <div
          style={{
            backgroundColor: "#f8f9fa",
            border: "2px solid #ebedf3",
          }}
        >
          {showPopup && (
            <div ref={columnsPanelRef} className={`side-panel ${showPopup ? "open" : ""}`} style={{overflow: "hidden"}}>
              <div className="panel-content" style={{ height: "100vh", padding: 0 }}>
                <div style={{borderBottom: "1px solid #dee2e6", padding: "20px", paddingTop: 0}}>
                  <div className="d-flex justify-content-end my-2">
                    <Button color="default" variant="filled" shape="circle" size='small' icon={<CloseOutlined />} onClick={togglePopup} />
                  </div>
                  <div className="mb-2">
                    <b className="fs-5">Row Groups</b>
                  </div>
                  {FilterField && (
                    <DndProvider backend={HTML5Backend}>
                      <div>
                        {fields.map((field, index) => (
                          <div key={field.key} style={{ position: "relative" }}>
                            <FilterField
                              index={index}
                              field={field}
                              moveField={moveField}
                              toggleCheck={toggleCheck}
                            />
                            {field.checked && (
                              <div style={{ position: "absolute", right: 8, top: 8, display: "flex", gap: 6 }}>
                                <Tooltip title={`Sort ${field.label} asc`}>
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<UpOutlined />}
                                    onClick={() => onRowGroupSortChange && onRowGroupSortChange(field.key, 'asc')}
                                    style={{ color: rowGroupSortDirections?.[field.key] === 'asc' ? '#52c41a' : '#8c8c8c' }}
                                  />
                                </Tooltip>
                                <Tooltip title={`Sort ${field.label} desc`}>
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<DownOutlined />}
                                    onClick={() => onRowGroupSortChange && onRowGroupSortChange(field.key, 'desc')}
                                    style={{ color: rowGroupSortDirections?.[field.key] === 'desc' ? '#52c41a' : '#8c8c8c' }}
                                  />
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </DndProvider>
                  )}
                  
                  {hasPendingRowGroupChanges && applyRowGroupChanges && (
                    <div className="d-flex justify-content-center mt-3">
                      <Button 
                        type="primary" 
                        size="small"
                        onClick={applyRowGroupChanges}
                      >
                        Apply
                      </Button>
                    </div>
                  )}
                </div>

                <div className="list-sort-move" style={{padding: "20px"}} onMouseDown={() => {
                  if (fieldListRef && fieldListRef.current) {
                    lastFieldListScrollTopRef.current = fieldListRef.current.scrollTop || 0;
                  }
                }}>
                  <div className="search-box d-flex align-items-center mb-3">
                    <div className="me-3 ms-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={getVisibleColumns().length === (columnOrder?.length || 0)}
                        onChange={(e) => setAllColumnsVisible && setAllColumnsVisible(e.target.checked)}
                        title="Check/Uncheck all columns"
                        style={{ position : 'initial' , marginTop : '0.3rem' , marginLeft : 0 }}
                      />
                    </div>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search fields..."
                        value={searchTerm || ""}
                        onChange={(e) => setSearchTerm && setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="field-list-move" ref={fieldListRef}>
                    {ColumnField && columnOrder && (
                      <DndProvider backend={HTML5Backend}>
                        <div>
                          {columnOrder
                            .filter((field) => (getFieldLabel(field) || "").toLowerCase().includes((searchTerm || "").toLowerCase()))
                            .map((field, index) => (
                              <ColumnField
                                key={field}
                                index={index}
                                field={field}
                                moveColumn={moveColumn}
                              />
                            ))}
                        </div>
                      </DndProvider>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {showFilterPanel && (
            <div ref={filtersPanelRef} className={`side-panel ${showFilterPanel ? "open" : ""}`}>
              <div className="panel-content" style={{ height: "100vh", padding: 0 }}>
                <div style={{padding: "20px",paddingTop: 0}}>
                  <div className="d-flex justify-content-end my-2">
                    <Button color="default" variant="filled" shape="circle" size='small' icon={<CloseOutlined />} onClick={() => setShowFilterPanel(false)} />
                  </div>
                  <div className="mb-2">
                    <b className="fs-5">Filters</b>
                  </div>
                  <div className="search-box mb-3">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search fields..."
                        value={filters.searchTerm}
                        onChange={(e) =>
                          props.setFilters({
                            ...filters,
                            searchTerm: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="field-list">
                    {props.filterableColumns && props.filterableColumns
                      .filter((field) => (getFieldLabel(field) || "").toLowerCase().includes((filters.searchTerm || "").toLowerCase()))
                      .map((field) => (
                        <div key={field} className="field-item">
                          <div
                            className="field-header d-flex justify-content-between align-items-center"
                            onClick={() => props.toggleFieldExpand(field)}
                          >
                            <span>{getFieldLabel(field)}</span>
                            <i className={`fas fa-chevron-${filters.expandedFields[field] ? "up" : "down"}`} />
                          </div>

                          {filters.expandedFields[field] && (
                            <div className="field-conditions p-2 bg-light rounded">
                              {props.renderFilterFieldControls(field)}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!showPopup && !showFilterPanel && (
            <div className="vertical-tabs">
              <button
                className={`vertical-tab ${showFilterPanel ? "active" : ""}`}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
              >
                <CIcon className="mb-2" icon={icon.cilListFilter} style={{ width: "16px", height: "16px" }} />
                <span>Filters</span>
              </button>
              <button
                className={`vertical-tab ${showPopup ? "active" : ""}`}
                onClick={togglePopup}
              >
                <CIcon className="mb-2" icon={icon.cilColumns} style={{ width: "16px", height: "16px" }} />
                <span>Columns</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default TableContainer;


